import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import {
  buildSignalWeightsRows,
  SignalWeightsTableFromPayload,
} from "../../Signals/components/SignalWeightsTable";

function safeStringifySignal(obj) {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
}

/** Safe segment for download filenames (Windows + general). */
function safeStrategyNameForFile(name) {
  const raw = String(name ?? "").trim();
  if (!raw) return "strategy";
  const cleaned = raw
    .replace(/[/\\:*?"<>|]+/g, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  return cleaned.slice(0, 120) || "strategy";
}

const detailPaperSx = {
  borderRadius: 2,
  border: 1,
  borderColor: "divider",
  overflow: "hidden",
};


export default function SignalCalendarDayDetailDialog({
  open,
  onBack,
  onCloseAll,
  detailYmd,
  strategyName = "",
  signalsByCalendarYmd = {},
}) {
  const theme = useTheme();
  const [copyMenuAnchor, setCopyMenuAnchor] = useState(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

  useEffect(() => {
    if (!open) {
      setCopyMenuAnchor(null);
      setDownloadMenuAnchor(null);
    }
  }, [open]);

  const detailSignals =
    detailYmd && Array.isArray(signalsByCalendarYmd[detailYmd])
      ? signalsByCalendarYmd[detailYmd]
      : [];

  const detailTableRows = useMemo(() => {
    if (!detailSignals.length) return [];
    const out = [];
    for (const sig of detailSignals) {
      if (!sig?.weights_keys || !sig?.weights_values) continue;
      out.push(
        ...buildSignalWeightsRows(sig.weights_keys, sig.weights_values)
      );
    }
    return out;
  }, [detailSignals]);

  const handleCopyForWebsite = useCallback(() => {
    if (!detailTableRows.length) {
      toast.error("No data available to copy");
      setCopyMenuAnchor(null);
      return;
    }
    try {
      navigator.clipboard.writeText(JSON.stringify(detailTableRows));
      toast.success("Copied signals data for Website!");
    } catch (error) {
      console.error("Error copying signals:", error);
      toast.error("Failed to copy signals");
    }
    setCopyMenuAnchor(null);
  }, [detailTableRows]);

  const handleCopyForExcel = useCallback(() => {
    if (!detailTableRows.length) {
      toast.error("No data available to copy");
      setCopyMenuAnchor(null);
      return;
    }
    try {
      const formattedData = detailTableRows
        .map((item) => `${item.name}\t${item.value}%`)
        .join("\n");
      navigator.clipboard.writeText(formattedData);
      toast.success("Copied signals data for Excel!");
    } catch (error) {
      console.error("Error copying signals:", error);
      toast.error("Failed to copy signals");
    }
    setCopyMenuAnchor(null);
  }, [detailTableRows]);

  const handleDownloadCsv = useCallback(() => {
    if (!detailTableRows.length || !detailYmd) {
      toast.error("No signal data available.");
      setDownloadMenuAnchor(null);
      return;
    }
    const csvRows = [
      ["Ticker", "Weighting %"],
      ...detailTableRows.map((r) => [r.name, `${r.value}%`]),
    ];
    const csvContent = csvRows
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");
    try {
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      const filename = `${safeStrategyNameForFile(strategyName)}_${detailYmd}.csv`;
      link.href = url;
      link.download = filename;
      link.rel = "noopener";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success("CSV file downloaded successfully.");
    } catch (error) {
      console.error("Error downloading CSV file:", error);
      toast.error("Failed to download CSV file.");
    }
    setDownloadMenuAnchor(null);
  }, [detailTableRows, detailYmd, strategyName]);

  const handleDownloadJson = useCallback(() => {
    if (!detailSignals.length || !detailYmd) {
      toast.error("No signal data available.");
      setDownloadMenuAnchor(null);
      return;
    }
    try {
      const text = JSON.stringify(detailSignals, null, 2);
      const blob = new Blob([text], { type: "application/json;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const base = safeStrategyNameForFile(strategyName);
      link.href = url;
      link.download = `${base}_${detailYmd}.json`;
      link.rel = "noopener";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success("File downloaded successfully.");
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file.");
    }
    setDownloadMenuAnchor(null);
  }, [detailSignals, detailYmd, strategyName]);

  const handleDialogClose = useCallback(
    (_event, reason) => {
      if (reason === "backdropClick" || reason === "escapeKeyDown") {
        onBack?.();
      }
    },
    [onBack]
  );

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: detailPaperSx,
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          py: 1.75,
          px: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, flex: 1 }}>
          <IconButton
            size="small"
            onClick={() => onBack?.()}
            aria-label="Back to calendar"
            sx={{ color: "text.secondary", flexShrink: 0 }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            component="div"
            sx={{ minWidth: 0 }}
          >
            Signal Entry
            {detailYmd ? (
              <>
                {" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 400,
                    color: "text.secondary",
                    fontSize: (t) => t.typography.caption.fontSize,
                    lineHeight: 1.3,
                    verticalAlign: "baseline",
                  }}
                >
                  {detailYmd}
                </Box>
              </>
            ) : null}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => onCloseAll?.()}
          aria-label="Close calendar and signal detail"
          sx={{ color: "text.secondary", flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2, pb: 2 }}>
        {detailSignals.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              justifyContent: "flex-end",
              mb: 2,
            }}
          >
            <Tooltip title="Copy Signals" arrow placement="top">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  disabled={!detailTableRows.length}
                  onClick={(e) => setCopyMenuAnchor(e.currentTarget)}
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    fontWeight: 500,
                    "&:hover": {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: `${theme.palette.primary.light}20`,
                      transform: "scale(1.02)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  Copy
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Download" arrow placement="top">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon fontSize="small" />}
                  onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    fontWeight: 500,
                    "&:hover": {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: `${theme.palette.primary.light}20`,
                      transform: "scale(1.02)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  Download
                </Button>
              </span>
            </Tooltip>
            <Menu
              anchorEl={copyMenuAnchor}
              open={Boolean(copyMenuAnchor)}
              onClose={() => setCopyMenuAnchor(null)}
              MenuListProps={{ "aria-labelledby": "signal-calendar-copy" }}
            >
              <MenuItem onClick={handleCopyForWebsite}>
                Copy for Website
              </MenuItem>
              <MenuItem onClick={handleCopyForExcel}>Copy for Excel</MenuItem>
            </Menu>
            <Menu
              anchorEl={downloadMenuAnchor}
              open={Boolean(downloadMenuAnchor)}
              onClose={() => setDownloadMenuAnchor(null)}
              MenuListProps={{ "aria-labelledby": "signal-calendar-download" }}
            >
              <MenuItem
                onClick={handleDownloadCsv}
                disabled={!detailTableRows.length}
              >
                Download CSV
              </MenuItem>
              <MenuItem onClick={handleDownloadJson}>Download JSON</MenuItem>
            </Menu>
          </Box>
        ) : null}
        {detailSignals.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No signal payload for this date.
          </Typography>
        ) : (
          detailSignals.map((sig, idx) => {
            const hasWeights =
              sig &&
              typeof sig === "object" &&
              sig.weights_keys &&
              sig.weights_values;
            return (
              <Box
                key={`${detailYmd}-${idx}`}
                sx={{ mb: idx < detailSignals.length - 1 ? 3 : 0 }}
              >
                {detailSignals.length > 1 ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 1 }}
                  >
                    Entry {idx + 1} of {detailSignals.length}
                  </Typography>
                ) : null}
                {hasWeights ? (
                  <Box
                    sx={{
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                      overflow: "hidden",
                    }}
                  >
                    <SignalWeightsTableFromPayload
                      weights_keys={sig.weights_keys}
                      weights_values={sig.weights_values}
                      showNewsColumn={false}
                      tableContainerSx={{
                        maxHeight: "min(55vh, 400px)",
                        overflowX: "auto",
                        overflowY: "auto",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                      fontSize: "0.75rem",
                      lineHeight: 1.5,
                      overflow: "auto",
                      maxHeight: "min(60vh, 420px)",
                      fontFamily: "ui-monospace, monospace",
                    }}
                  >
                    {safeStringifySignal(sig)}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </DialogContent>
    </Dialog>
  );
}
